import React from 'react';
import { X, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const Cart = () => {
  const { cartItems, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, total, clearCart } = useCart();
  const { currentTheme } = useTheme();

  // Check for welcome discount (15% or 20%) - ONE TIME USE ONLY
  const welcomeEmail = localStorage.getItem('welcomeEmail');
  const usedDiscountKey = welcomeEmail ? `usedDiscount_${welcomeEmail}` : null;
  const hasAlreadyUsedDiscount = usedDiscountKey ? localStorage.getItem(usedDiscountKey) : null;
  
  const hasWelcomeDiscount = localStorage.getItem('welcomeSignup') === 'true' && 
                             !hasAlreadyUsedDiscount;
  const discountCode = localStorage.getItem('discountCode');
  const discountRate = discountCode === 'LOYALTY20' ? 0.20 : 0.15;
  const discount = hasWelcomeDiscount ? total * discountRate : 0;
  const finalTotal = total - discount;

  const [isProcessing, setIsProcessing] = React.useState(false);

  const handleCheckout = async () => {
    setIsProcessing(true);
    try {
      if (hasWelcomeDiscount && usedDiscountKey) {
        localStorage.setItem(usedDiscountKey, discountCode);
        localStorage.setItem('discountUsed', 'true');
      }

      const items = cartItems.map(item => ({
        id: item.id,
        type: item.type,
        quantity: item.quantity,
        license_name: item.selectedLicense ? item.selectedLicense.name : null,
        license_index: item.selectedLicense ? 0 : 0,
      }));

      let response;
      try {
        response = await fetch('/api/payments/checkout/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            discount_code: hasWelcomeDiscount ? discountCode : null,
          }),
        });
      } catch (networkErr) {
        // Network error - backend might be down
        toast({
          title: "Connection Error",
          description: "Can't reach the server. Please try again in a moment.",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        const msg = error.detail || 'Checkout failed';
        // Show user-friendly message for Stripe issues
        if (msg.includes('Stripe not configured') || msg.includes('Failed to create checkout')) {
          toast({
            title: "Payments Coming Soon! 🚀",
            description: "Our payment system is being set up. Please check back in a couple days! Contact us directly for immediate purchases.",
          });
        } else {
          toast({
            title: "Checkout Error",
            description: msg,
            variant: "destructive",
          });
        }
        setIsProcessing(false);
        return;
      }

      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      toast({
        title: "Checkout Error",
        description: err.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Cart Sidebar */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-zinc-950 z-50 shadow-2xl flex flex-col border-l border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold text-white">Your Cart</h2>
          <button
            onClick={() => setIsCartOpen(false)}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">Your cart is empty</p>
            </div>
          ) : (
            cartItems.map(item => (
              <div key={`${item.id}-${item.type}`} className="flex gap-4 bg-white/5 p-4 rounded-lg">
                <img 
                  src={item.imageUrl} 
                  alt={item.title || item.name}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-white font-medium text-sm">{item.title || item.name}</h3>
                  <p className="text-gray-400 text-xs mt-1">
                    {item.type === 'beat' ? `${item.genre} • ${item.bpm} BPM` : item.category}
                  </p>
                  <p className="text-white font-bold mt-2">${item.price.toFixed(2)}</p>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateQuantity(item.id, item.type, item.quantity - 1)}
                      className="w-6 h-6 rounded flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <Minus className="w-3 h-3 text-white" />
                    </button>
                    <span className="text-white w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.type, item.quantity + 1)}
                      className="w-6 h-6 rounded flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <Plus className="w-3 h-3 text-white" />
                    </button>
                    <button
                      onClick={() => removeFromCart(item.id, item.type)}
                      className="ml-auto text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-white/10 p-6 space-y-4">
            {hasWelcomeDiscount && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-green-400 font-bold text-sm">🎉 ONE-TIME OFFER</div>
                    <div className="text-green-400/70 text-xs">Code: {discountCode}</div>
                  </div>
                  <span className="text-green-400 font-bold text-lg">-{Math.round(discountRate * 100)}%</span>
                </div>
                <div className="flex justify-between text-sm border-t border-green-500/20 pt-2">
                  <span className="text-green-400">Subtotal</span>
                  <span className="text-green-400">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-400">Discount</span>
                  <span className="text-green-400">-${discount.toFixed(2)}</span>
                </div>
              </div>
            )}
            
            {hasAlreadyUsedDiscount && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                <div className="text-yellow-400 text-xs text-center">
                  ⚠️ You've already used your one-time welcome discount
                </div>
              </div>
            )}
            <div className="flex justify-between text-lg">
              <span className="text-gray-400">Total</span>
              <span className="text-white font-bold">${finalTotal.toFixed(2)}</span>
            </div>
            <Button
              onClick={handleCheckout}
              className="w-full h-12 text-black font-bold text-lg hover:scale-105 transition-transform"
              style={{ backgroundColor: currentTheme.primary }}
            >
              {isProcessing ? 'Processing...' : 'Checkout'}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default Cart;